/** Imports */
import {z} from 'zod';
import {zMedia} from '@/mongoSchemas/Post/Medias.zod';
import {zMongoObject, zMongoObjectId} from '@/types/utils';
import {zTikTokCreatorInfo} from '@/mongoSchemas/TikTokCreatorInfo.zod';
import {zOrganization} from '@/mongoSchemas/Organization/Organizations.zod';
import {zTikTokAnchorEligibility} from '@/mongoSchemas/TikTokAnchorEligibility.zod';


/** Types */
export type MetaAccessTokenDebug = z.infer<typeof zMetaAccessTokenDebug>;
export type AccessToken = z.infer<typeof zIAccessTokenSchema>;
export type Page = z.infer<typeof zPage>;
export type Platform = z.infer<typeof zPlatform>;


/// Zod schemas
export const zPlatform = z.enum([
    'facebook',
    'instagram',
    'tiktok',
    'youtube',
    'threads',
    'twitter',
]);
export const zMetaAccessTokenDebug = z.object({
    app_id: z.string().optional(),
    type: z.string(),
    application: z.string(),
    data_access_expires_at: z.number(),
    expires_at: z.number(),
    is_valid: z.boolean(),
    issued_at: z.number(),
    profile_id: z.string().optional(),
    scopes: z.string().array(),
    granular_scopes: z.object({
        scope: z.string(),
    }).array().optional(),
    user_id: z.string(),
    updatedAt: z.coerce.date().optional(),
});
const zIAccessTokenSchema = z.object({
    accessToken: z.string(),
    secret: z.string().optional(),
    scopes: z.string().array().optional(),
    expiresAt: z.instanceof(Date).optional(),
    dataAccessExpiresAt: z.instanceof(Date).optional(),
    debug: zMetaAccessTokenDebug.optional(),
});
export const zPage = zMongoObject.extend({

    // Main
    get organization() {
        return z.union([
            zMongoObjectId,
            zOrganization,
        ]);
    },
    socialMediaPlatform: zPlatform,
    pageName: z.string(),
    pageId: z.string(),
    pagePicture: z.string(),
    linkToProfile: z.string(),
    accessToken: zIAccessTokenSchema.optional(),
    refreshToken: z.string().optional(),
    pageIsPremium: z.boolean().optional(),

    // Parents
    parentPageId: z.string().optional(),
    parentPageName: z.string().optional(),
    parentPagePicture: z.string().optional(),

    // Data
    pageAdded: z.instanceof(Date).optional(),
    lastTimeAuthorized: z.instanceof(Date).optional(),
    tiktokCreatorInfo: zTikTokCreatorInfo.optional(),
    tiktokCreatorInfoUpdatedAt: z.instanceof(Date).optional(),
    tiktokAnchorEligibility: zTikTokAnchorEligibility.optional(),
    // Options
    overlay: zMedia.optional(),
    brandingColor: z.string().optional(),

    // Flags
    hasError: z.boolean().optional(),
    hasSpamWarning: z.boolean().optional(),
    errorDescription: z.string().optional(),
    isDev: z.boolean().optional(),
    isPrivate: z.boolean().optional(),
});
